import React, { useState } from "react";
import { ExternalLink, FlaskConical } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Renders an inline "study" trigger that opens a slick in-page modal with a
 * plain-English summary of the research, its citation, and a link out to the
 * source on PubMed / PMC — instead of jumping the shopper straight off-site.
 *
 * `ingredient` must carry: { name, dose, category, study: { label, url, citation, summary } }
 */
export const StudyDialog = ({ ingredient, testid, sampleTheme = false, mobileSafe = false }) => {
  const [open, setOpen] = useState(false);
  const study = ingredient?.study;
  if (!study) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group mt-2 flex items-start gap-1.5 label !text-[9.5px] !tracking-[0.12em] hover:opacity-70 transition-opacity duration-200 text-left ${sampleTheme ? "sample-study-link sample-accent" : "!text-[#4a5941]"}`}
        data-testid={testid}
      >
        <FlaskConical size={11} strokeWidth={1.6} className="mt-[1px] shrink-0" />
        <span className={`min-w-0 underline underline-offset-2 ${sampleTheme ? "decoration-current" : "decoration-[#4a5941]/40"}`}>{study.label}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`max-w-lg bg-[color:var(--bg)] border-[color:var(--line)] rounded-none p-0 ${sampleTheme ? "sample-experience" : ""} ${sampleTheme || mobileSafe ? "w-[calc(100%_-_2rem)] max-h-[90dvh] overflow-y-auto" : "overflow-hidden"}`} data-testid="study-dialog">
          <div className="bg-[#0f0f10] text-[#faf9f5] px-7 pt-7 pb-6">
            <div className="label !text-[9.5px] !text-[#faf9f5]/60" data-testid="study-dialog-category">{ingredient.category} · The Science</div>
            <DialogHeader className="mt-2 space-y-0 text-left">
              <DialogTitle className="font-display text-2xl md:text-3xl tracking-[-0.02em] leading-tight text-[#faf9f5]" data-testid="study-dialog-title">
                {ingredient.name}
              </DialogTitle>
            </DialogHeader>
            {ingredient.show_dose && ingredient.dose && (
              <div className={`mt-3 inline-block font-mono tabular-nums text-[13px] font-semibold px-2 py-0.5 bg-[#a8c69f]/25 border border-[#a8c69f]/50 ${sampleTheme ? "sample-accent" : "text-[#a8c69f]"}`} data-testid="study-dialog-dose">
                {ingredient.dose}
              </div>
            )}
          </div>

          <div className="px-7 py-6">
            <div className="label !text-[9.5px] !text-[color:var(--ink-3)] mb-2">What the research found</div>
            <DialogDescription className="text-[15px] leading-relaxed text-[color:var(--ink)]" data-testid="study-dialog-summary">{study.summary}</DialogDescription>

            {study.citation && (
              <div className="mt-5 pt-4 border-t border-[color:var(--line)]">
                <div className="label !text-[9.5px] !text-[color:var(--ink-3)] mb-1">Citation</div>
                <div className="text-[13px] text-[color:var(--ink-2)] italic" data-testid="study-dialog-citation">{study.citation}</div>
              </div>
            )}

            <a
              href={study.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.2em] hover:bg-[#2a2a2a] transition"
              data-testid="study-dialog-source"
            >
              Read the full study
              <ExternalLink size={13} strokeWidth={1.6} />
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudyDialog;
